/**
 * Plan and billing, for any screen that needs it: the `useBilling` hook, a
 * link to wherever this user changes plan, and the account menu for people
 * signed in at the app's own address.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { buttonClass } from "@/components/ui";
import { useTour } from "@/components/tour/Tour";
import { api, errorText } from "./api";
import { useRumioSession } from "./session";

export interface Billing {
  tier: string;
  label: string;
  is_trial: boolean;
  /** AI campaigns allowed this month (or this trial); null = unlimited. */
  campaigns_per_month: number | null;
  billing_source: "rumio" | "standalone";
  /** Rumio users: the tenant site's billing page. */
  manage_url?: string;
  email?: string | null;
  interval?: "monthly" | "annual" | null;
  trial_ends_at?: string | null;
  renews_at?: string | null;
  cancels_at_period_end?: boolean;
  payment_failed?: boolean;
  can_manage?: boolean;
  can_subscribe?: boolean;
  /** Standalone: false for someone on the account who isn't its owner (billing is the owner's). */
  is_owner?: boolean;
  /** Standalone: had a subscription or trial before, so a new one has no trial. */
  ever_subscribed?: boolean;
}

/** "Yearly — 2 months free", from the prices the server sends (a year is billed as fewer months). */
export function yearlyLabel(plans: { monthly_usd: number; annual_usd: number }[] | null | undefined): string {
  const p = plans?.find((x) => x.monthly_usd > 0);
  const free = p ? Math.round(12 - p.annual_usd / p.monthly_usd) : 0;
  return free > 0 ? `Yearly — ${free} month${free === 1 ? "" : "s"} free` : "Yearly";
}

export function useBilling() {
  const [billing, setBilling] = useState<Billing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await api.get<{ data: Billing }>("/api/v1/billing");
      setBilling(res.data);
      setError(null);
      return res.data;
    } catch (err) {
      setError(errorText(err, "We couldn't load your plan."));
      return null;
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { billing, error, reload };
}

/**
 * A link to where this user picks or changes a plan: the Billing screen,
 * which sends a Rumio user on to the tenant site and a standalone account to
 * Stripe. Use it wherever the app says "upgrade" or "choose a plan".
 */
export function UpgradeLink({ children = "Upgrade", className }: { children?: ReactNode; className?: string }) {
  return (
    <Link to="/billing" className={className ?? "font-medium underline"}>
      {children}
    </Link>
  );
}

/** Who is signed in, with a way out. Renders nothing for a Rumio user: the host site owns their session. */
export function AccountMenu({ email, className }: { email?: string | null; className?: string }) {
  const { standalone, signOut, claims } = useRumioSession();
  const tour = useTour();
  if (!standalone) return null;
  // The session names the person signed in; `email` is the account's address, for older sessions.
  const who = claims?.email ?? email;
  return (
    <div className={className ?? "flex items-center justify-between gap-3 text-sm"}>
      {who && <span className="truncate text-muted-foreground">{who}</span>}
      {claims?.admin && (
        <Link to="/admin" className="font-medium underline">
          Operator
        </Link>
      )}
      <button type="button" disabled={tour.active} onClick={tour.start} className="text-left font-medium underline disabled:opacity-50">
        Take the tour
      </button>
      <button
        type="button"
        onClick={() => {
          signOut();
          window.location.assign("/login");
        }}
        className={buttonClass("secondary")}
      >
        Sign out
      </button>
    </div>
  );
}
